import React, { useEffect, useState } from 'react'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { useIntl } from 'react-intl'
import moment from 'moment-timezone'

import {
  Panel,
  Grid,
  GridCell,
  Flex,
  FlexItem,
  Title,
  Text,
  TEXT_VARIANT_LIGHT,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SpacingVertical,
  SPACING_4,
  SkeletonLoader,
  Badge,
  HelpIcon,
  SPACING_8,
  TITLE_VARIANT_M
} from 'mailup-web-components'
import iconSendQueueTime from 'Img/icon-send-queue-time.svg'
import iconSendQueueRecipients from 'Img/icon-send-queue-recipients.svg'
import { timeAndCountState } from '../recoil/state/campaign'
import aiLightningWhite from 'Img/ai-lightning-white.svg'

import iconSmartPlanning from 'Img/icon-smart-planning.svg'

import {
  evalCondition,
  notifyParent,
  storeGet,
  STORE_KEY_ACCOUNT,
  getLang,
  STORE_KEY_FEATURES,
  fromUTCtoZoneFormatted,
  STORE_KEY_LIST
} from 'mailup-react-helpers'
import styled from '@emotion/styled'
import { smartPlanningState } from '../recoil/state/page'
import { getSmartPlanningSuggestedDateCallback } from '../recoil/callbacks/callbacks'
import { isScheduledSendingState } from '../recoil/state/sender'
import { MODE_SMART } from 'Constants/index'

const EstimatedTimeBox = () => {
  const intl = useIntl()

  const showLinkECommerce = evalCondition(
    'NOT c:IsRetailer AND NOT c:IsDeveloper AND uo:GEST AND NOT co:TURNOFFMSHOP'
  )
  const { IsSendMethodTypeFrequency } = storeGet(STORE_KEY_ACCOUNT)

  const { estimatedTime, recipientCount } = useRecoilValue(timeAndCountState)

  const {
    smartPlanningDate,
    smartPlanningDateLoaded,
    smartPlanningError
  } = useRecoilValue(smartPlanningState)

  const setIsScheduledSendingState = useSetRecoilState(isScheduledSendingState)

  const getSmartPlanningSuggestedDate = useRecoilCallback(
    getSmartPlanningSuggestedDateCallback,
    []
  )
  const tzcode = storeGet(STORE_KEY_LIST).TimeZoneCode

  const handleUpsellClick = () => {
    notifyParent('redirectMainFrame', [
      '/console/mshop_Redirector.aspx?t=mshopem'
    ])
  }

  const smartPlanningFeature =
    storeGet(STORE_KEY_FEATURES).indexOf('smartPlanning') >= 0
  const account = storeGet(STORE_KEY_ACCOUNT)
  const showSmartPlanning =
    smartPlanningFeature &&
    account.IdOffer !== 3 &&
    account.IdOffer !== 2 &&
    account.IdOffer !== 6 &&
    !account.IsOfferLight // NON E' UNA STARTER - FREQUENZA - SOLOSMS-LIGHT

  // ESTIMATED TIME
  const [ft, setFtime] = useState()

  const formatTime = () => {
    const lang = getLang()

    const secondsTime = moment.duration(estimatedTime, 'seconds')
    const d = secondsTime.days()
    const h = secondsTime.hours()
    const m = secondsTime.minutes()
    const s = secondsTime.seconds()

    return `${intl.formatNumber(d)}${lang === 'it' ? 'g' : 'd'} ${String(
      h
    ).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(
      2,
      '0'
    )}s`
  }

  useEffect(() => {
    setFtime(formatTime())
  }, [estimatedTime])

  useEffect(() => {
    if (smartPlanningFeature) {
      getSmartPlanningSuggestedDate(false)
    }
  }, [smartPlanningFeature])

  const applySmartPlanning = () => {
    setIsScheduledSendingState(prev => ({
      ...prev,
      isScheduledSending: true,
      scheduleModeSelected: MODE_SMART
    }))
  }

  return (
    <Panel data-mu-test='email-sending-queue-manage__estimated-time-and-total-recipient-panel'>
      <Grid cols={12}>
        <GridCell span={showSmartPlanning ? 4 : 6}>
          <Flex padding='16px 0 8px 0'>
            <FlexItem padding='0 16px 8px 0'>
              <img src={iconSendQueueRecipients} />
            </FlexItem>
            <FlexItem>
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({ id: 'Load.Queue.EstimatedTime' })}
              </Text>
              <SpacingVertical variant={SPACING_8} />
              {ft ? (
                <Title variant={TITLE_VARIANT_M}> {ft} </Title>
              ) : (
                <SkeletonLoader width='250px' height='32px' />
              )}

              {showLinkECommerce && (
                <>
                  <SpacingVertical variant={SPACING_8} />
                  <Link
                    variant={LINK_VARIANT_HIGHLIGHT}
                    onClick={handleUpsellClick}
                    data-mu-test='email-sending-queue-manage__upsell-link'
                  >
                    {IsSendMethodTypeFrequency
                      ? intl.formatMessage({
                          id: 'Load.Queue.TempoStimatoUpsellingFrequenzaIntl'
                        })
                      : intl.formatMessage({
                          id: 'Load.Queue.TempoStimatoUpsellingBandaIntl'
                        })}
                  </Link>
                </>
              )}
            </FlexItem>
          </Flex>
        </GridCell>

        <GridCell span={showSmartPlanning ? 4 : 6}>
          <Flex padding='16px 0 8px 0'>
            <FlexItem padding='0 16px 8px 0'>
              <img src={iconSendQueueTime} />
            </FlexItem>
            <FlexItem>
              <Text variant={TEXT_VARIANT_LIGHT}>
                {intl.formatMessage({ id: 'Load.QueueStatus.RecipientsTotal' })}
              </Text>
              <SpacingVertical variant={SPACING_8} />
              {recipientCount ? (
                <Title variant={TITLE_VARIANT_M}>
                  {intl.formatNumber(recipientCount)}
                </Title>
              ) : (
                <SkeletonLoader width='250px' height='32px' />
              )}
            </FlexItem>
          </Flex>
        </GridCell>
        {showSmartPlanning && (
          <GridCell span={4}>
            <Flex padding='16px 0 8px 0'>
              <StyledFlexItem padding='0 16px 8px 0'>
                <img src={iconSmartPlanning} />
              </StyledFlexItem>
              <Flex flexDirection='column'>
                <Flex alignItems='center' gap='4px'>
                  <Text variant='light'>
                    {intl.formatMessage({
                      id: 'Load.QueueStatus.AiPlanningTitle'
                    })}
                  </Text>
                  <StyledBadgeAIGradient
                    type='info'
                    kind='default'
                    text={
                      <Flex alignItems='center' gap='2px'>
                        BETA AI
                        <img src={aiLightningWhite} />
                      </Flex>
                    }
                  />
                  <HelpIcon
                    text={intl.formatMessage({
                      id: 'Load.QueueStatus.AiPlanningTooltip'
                    })}
                  />
                </Flex>
                <SpacingVertical variant={SPACING_8} />
                {smartPlanningDateLoaded ? (
                  <>
                    {!smartPlanningError && (
                      <>
                        {smartPlanningDate && (
                          <Flex gap='4px' alignItems='baseline'>
                            <Title variant={TITLE_VARIANT_M}>
                              {fromUTCtoZoneFormatted(
                                smartPlanningDate,
                                tzcode,
                                'YYYY/MM/DD - HH:mm'
                              )}
                            </Title>
                            <Text variant='small-light'>{`(${
                              tzcode.split('.')[0]
                            })`}</Text>
                          </Flex>
                        )}
                        {!smartPlanningDate && (
                          <Title variant={TITLE_VARIANT_M}>-</Title>
                        )}
                      </>
                    )}
                    {smartPlanningError === 'NoData' && (
                      <Title variant={TITLE_VARIANT_M}>
                        {intl.formatMessage({
                          id: 'Load.QueueStatus.AiPlanningNoData'
                        })}
                      </Title>
                    )}
                    {smartPlanningError === 'DataError' && (
                      <Title variant={TITLE_VARIANT_M}>
                        {intl.formatMessage({
                          id: 'Load.QueueStatus.AiPlanningDataError'
                        })}
                      </Title>
                    )}
                  </>
                ) : (
                  <SkeletonLoader width='200px' height='18px' />
                )}
                <SpacingVertical variant={SPACING_8} />
                <Link
                  disabled={!smartPlanningDateLoaded || smartPlanningError}
                  variant={LINK_VARIANT_HIGHLIGHT}
                  onClick={applySmartPlanning}
                  data-mu-test='link-AiPlanningApply'
                >
                  {intl.formatMessage({
                    id: 'Load.QueueStatus.AiPlanningApply'
                  })}
                </Link>
              </Flex>
            </Flex>
          </GridCell>
        )}
      </Grid>
    </Panel>
  )
}

export default EstimatedTimeBox

const StyledFlexItem = styled(FlexItem)`
  position: relative;
`

const StyledBadgeAIGradient = styled(Badge)`
  font-size: 11px;
  padding: 8px 6px;
  background-color: white;
  border: 0;
  color: white;
  background: linear-gradient(to right, #762eef, #3dc6f5);
  height: 19px;
`
